import { useMemo } from 'react';
import { format, parseISO, compareAsc } from 'date-fns';
import Paper from '@mui/material/Paper';
import TableContainer from '@mui/material/TableContainer';
import { Typography } from '@components/Typography';
import Bar from '@components/Charts/Bar';
import { I18N_DATE_FORMAT } from '@helpers/dates';
import { useTranslation } from '@hooks';
import { styled } from '@mui/system';

const PaperStyled = styled(Paper)({
    width: '100%',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '4px',
    marginBottom: '16px',
    marginTop: 0,
    '@media print': {
        breakInside: 'avoid',
    },
});
const ChartsGrid = styled('div')({
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    '@media (max-width: 768px)': {
        gridTemplateColumns: '1fr',
    },
});

const ChartWrapper = styled('div')({
    position: 'relative',
    height: '280px',
    '@media print': {
        height: '280px !important',
    },
});

const createChartData = (data, legends) => {
    return {
        labels: data.labels,
        datasets: [
            {
                type: 'line',
                yAxisID: 'line',
                label: legends[1],
                data: data.averageRating,
                backgroundColor: '#1F4C5C',
                borderColor: '#1F4C5C',
                hoverBorderColor: '#1F4C5C',
                fill: false,
                borderWidth: 2,
                pointRadius: 6,
                pointHoverRadius: 12,
                pointBackgroundColor: '#BCC9CE',
                pointBorderColor: '#1F4C5C',
                pointBorderWidth: 1,
            },
            {
                type: 'bar',
                yAxisID: 'bar',
                label: legends[0],
                data: data.responsesCount,
                backgroundColor: 'rgba(189, 224, 204, 1)',
                hoverBackgroundColor: 'rgba(189, 224, 204, 1)',
                categoryPercentage: 1.0,
                barPercentage: 0.7,
                borderWidth: 0,
                borderRadius: 4,
            },
        ],
    };
};

const createChartOptions = (total, averageRating) => {
    return {
        elements: {
            point: {
                radius: 0,
            },
            line: {
                tension: 0.4,
                pointStyle: 'circle',
            },
        },
        plugins: {
            chartAreaBorder: null,
        },
        scales: {
            y: {
                display: false,
            },
            bar: {
                type: 'linear',
                position: 'left',
                border: {
                    display: true,
                    color: 'rgba(0, 0, 0, 0.3)',
                },
                grid: {
                    display: false,
                    tickLength: 0,
                    drawTicks: false,
                },
                ticks: {
                    stepSize: 5,
                    font: {
                        family: 'Rubik',
                        size: 10,
                    },
                    color: 'rgba(0, 0, 0, 0.6)',
                    padding: 12,
                },
                title: {
                    display: true,
                    position: 'left',
                    align: 'center',
                    font: {
                        family: 'Rubik',
                        size: 10,
                        weight: 500,
                    },
                    color: 'rgba(0, 0, 0, 0.6)',
                    text: total.toUpperCase(),
                    padding: {
                        bottom: 0,
                    },
                },
            },
            line: {
                type: 'linear',
                position: 'right',
                ticks: {
                    stepSize: 1,
                    font: {
                        family: 'Rubik',
                        size: 10,
                    },
                    color: 'rgba(0, 0, 0, 0.6)',
                    padding: 10,
                },
                border: {
                    display: true,
                    color: 'rgba(0, 0, 0, 0.3)',
                    dash: [5, 5],
                },
                grid: {
                    tickLength: 0,
                    drawTicks: false,
                },
                title: {
                    display: true,
                    position: 'left',
                    align: 'center',
                    font: {
                        family: 'Rubik',
                        size: 10,
                        weight: 500,
                    },
                    color: 'rgba(0, 0, 0, 0.6)',
                    text: averageRating.toUpperCase(),
                    padding: {
                        bottom: 0,
                    },
                },
            },
        },
    };
};

const Questions = ({ data, language }) => {
    const { t } = useTranslation();

    const chartData = useMemo(
        () =>
            Object.entries(data).reduce((acc, [question, value]) => {
                const sortedValue = value.sort((a, b) => compareAsc(parseISO(a.date), parseISO(b.date)));
                return [
                    ...acc,
                    {
                        question,
                        labels: sortedValue.map(({ date }) =>
                            format(parseISO(date), 'dd MMM', { locale: I18N_DATE_FORMAT[language] }),
                        ),
                        responsesCount: sortedValue.map(({ responsesCount }) => responsesCount),
                        averageRating: sortedValue.map(({ averageRating }) => averageRating),
                    },
                ];
            }, []),
        [data, language],
    );

    const legends = [t('AnalyticsReviews.totalResponses'), t('AnalyticsReviews.averageRating')];

    return (
        <PaperStyled elevation={1}>
            <Typography variant="title1" sx={{ color: 'rgba(0,0,0, 0.87)', marginBottom: '24px' }}>
                {t('FeedbackSurveys.surveyQuestions')}
            </Typography>
            <ChartsGrid>
                {chartData.map((item, index) => (
                    <TableContainer key={index}>
                        <Typography variant="h6" sx={{ color: 'rgba(0,0,0, 0.87)', transform: 'translateX(5px)' }}>
                            {item.question}
                        </Typography>
                        <ChartWrapper id="questions-chart-wrapper">
                            {item.responsesCount.length && item.averageRating.length && (
                                <Bar
                                    parentId="questions-chart-wrapper"
                                    data={createChartData(item, legends)}
                                    labels={item.labels}
                                    optionsProps={createChartOptions(
                                        t('AnalyticsReviews.totalResponses'),
                                        t('AnalyticsReviews.averageRating'),
                                    )}
                                />
                            )}
                        </ChartWrapper>
                    </TableContainer>
                ))}
            </ChartsGrid>
        </PaperStyled>
    );
};

export default Questions;
